import React, { useEffect, useState } from "react";
import { Cart } from "@leafly-com/reservations-api";

import { deleteCartPromoCode } from "api/requests/deleteCartPromoCode";
import { postCartPromoCode } from "api/requests/postCartPromoCode";
import { useDispatch, useSelector } from "redux/hooks";
import { UPDATE_CART_SUCCESS } from "redux/reducers/cart";
import { getCart } from "redux/selectors/cart";

import Spinner from "components/Icons/Spinner";
const MAX_ERROR_STR_LENGTH = 48;

const PromoCodeInput = () => {
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [promoCodeUpdating, setPromoCodeUpdating] = useState(false);
  const cart = useSelector(getCart);
  const cartId = cart?.data?.id;
  const dispatch = useDispatch();
  const promoCodes = cart?.data?.promoCodes ?? [];
  const isPromoCodeApplied = promoCodes.length > 0;
  const [cartPromoCode, setCartPromoCode] = useState<string | undefined>("");
  const [error, setError] = useState("");

  useEffect(() => {
    const promoCodeInfo = promoCodes[promoCodes.length - 1] ?? {};
    const appliedPromoCode = promoCodeInfo?.code ?? "";
    const promoQualified = promoCodeInfo?.qualified;

    if (!promoQualified && appliedPromoCode.length > 0) {
      setError("The contents of your cart do not qualify for the promo code");
    } else {
      setError("");
    }

    setPromoCodeInput(appliedPromoCode);
    setCartPromoCode(appliedPromoCode);
    setPromoCodeUpdating(false);
  }, [promoCodes]);

  const applyPromoCode = (promoCode: string) => {
    if (!cartId || !promoCode || isPromoCodeApplied) {
      handlePromoCodeChangeError(new Error("Unable to add promo code"));
      return;
    }
    postCartPromoCode({ cartId, promoCode })
      .then(handlePromoCodeChangeResponse)
      .catch(handlePromoCodeChangeError);
  };

  const removePromoCode = () => {
    const promoCodeId = promoCodes[promoCodes.length - 1]?.id;
    if (!cartId || !promoCodeId || !isPromoCodeApplied) {
      handlePromoCodeChangeError(new Error("Unable to remove promo code"));
      return;
    }
    deleteCartPromoCode({ cartId, promoCodeId })
      .then(handlePromoCodeChangeResponse)
      .catch(handlePromoCodeChangeError);
  };

  const handlePromoCodeChangeResponse = (response: Cart) => {
    if (response.promoCodes) {
      setError("");
      dispatch({
        // @ts-ignore -- TODO: fix types on this dispatch call (https://leafly.atlassian.net/browse/SHH-1112)
        cart: response,
        type: UPDATE_CART_SUCCESS,
      });
    }
    setPromoCodeUpdating(false);
  };

  const handlePromoCodeChangeError = (error: Error) => {
    setError(error.message);
    setPromoCodeUpdating(false);
  };

  const onPromoCodeSubmit = (changeType: "remove" | "apply", code?: string) => {
    setError("");
    setPromoCodeUpdating(true);

    if (changeType === "apply") {
      applyPromoCode(code ?? "");
    } else {
      removePromoCode();
    }
  };

  return (
    <>
      <label htmlFor="promo-code-input">Promo code</label>
      <div className="relative">
        <input
          style={{
            border: error ? "1px solid red" : "",
          }}
          disabled={isPromoCodeApplied}
          id="promo-code-input"
          data-testid="promo-code-input"
          aria-label="search"
          value={isPromoCodeApplied ? cartPromoCode : promoCodeInput}
          onChange={(e) => {
            setPromoCodeInput(
              e.target.value
                .replace(/[^a-zA-Z0-9]/g, "")
                .substring(0, 25)
                .toUpperCase(),
            );
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && promoCodeInput) {
              onPromoCodeSubmit("apply", promoCodeInput);
            }
          }}
          placeholder="Enter code"
        />
        <div className="absolute top-0 right-0 flex h-full text-xs text-green underline font-bold">
          {promoCodeUpdating ? (
            <span className="px-md flex items-center">
              <Spinner height={16} width={16} padding={false} />
            </span>
          ) : isPromoCodeApplied ? (
            <button
              aria-label="remove"
              className="px-md"
              data-testid="promo-code-action-remove"
              onClick={() => onPromoCodeSubmit("remove")}
            >
              remove
            </button>
          ) : (
            <button
              disabled={!promoCodeInput}
              aria-label="apply"
              className="px-md"
              data-testid="promo-code-action-apply"
              onClick={() => onPromoCodeSubmit("apply", promoCodeInput)}
            >
              apply
            </button>
          )}
        </div>
        {error && (
          <div className="relative">
            <div className="absolute flex">
              <span
                data-testid="promo-code-error"
                className="text-xs text-error items-center"
              >
                {error}
              </span>
            </div>
          </div>
        )}
      </div>
      <hr
        className={`border-light-grey ${
          error
            ? error.length < MAX_ERROR_STR_LENGTH
              ? "mt-sm"
              : "mt-xl"
            : "mt-0"
        }`}
      />
    </>
  );
};

export default PromoCodeInput;
